"use client";

import mouthpieces from "@/styles/modules/mouthpieces.module.scss";
import React, {useEffect, useRef} from "react";
import * as P from "@parts";
import { SnaptixHidden } from "@/api/snaptix";
import VadImage from "vadimages-nextjs-image-optimizer";

export type MouthpiecesWrapperBlockProps = {
    header: any;
    list: Mouthpiece[];
    features: MouthpieceFeatureList[];
    /** Centered footnote rendered under the grid, e.g. the "*Custom only" caption. Pre-rendered server-side. */
    footer?: React.ReactNode;
}

const mouthpieceCategories = ["Plastic", "Ceramic", "Hemp", "Custom"] as const;

export type MouthpieceCategory = (typeof mouthpieceCategories)[number];

export type Mouthpiece = {
    imgSrc: string;
    imgWidth: number;
    imgHeight: number;
    imgPreviewSrc: string;
    imgPreviewWidth: number;
    imgPreviewHeight: number;
    caption: string;
    category: MouthpieceCategory | MouthpieceCategory[];
    /** Snaptix mouthpieces swap between an open and a closed render on hover. Resolved server-side. */
    imgSnaptixSrc?: { open: string; closed: string };
    imgPreviewSnaptixSrc?: { open: string; closed: string };
}
export type MouthpieceFeatureList = {
    imgSrc: string;
    imgWidth: number;
    imgHeight: number;
    imgAlt: string;
    content: any;
    pretitle: any;
}

interface State
{
    allItems: Mouthpiece[];
    items: Mouthpiece[];
    category?: MouthpieceCategory;
    selectedItem?: Mouthpiece;
    /**
     * True once the visitor picks a mouthpiece. Until then nothing is highlighted: the
     * default selection exists only to give the hero image something to show, and bolding
     * that first caption read as a mistake rather than as state.
     */
    picked?: boolean;
}

/** Marks an item as picked by the visitor, which is what the highlight tracks. */
const pickItem = (item: Mouthpiece) => (prevState: State): State =>
    prevState.selectedItem === item && prevState.picked === true
        ? prevState
        : { ...prevState, selectedItem: item, picked: true };

interface DesktopMouthpieceButtonProps {
    item: Mouthpiece;
    state: State;
    setState: React.Dispatch<React.SetStateAction<State>>;
}

const DesktopMouthpieceButton = ({ item, state, setState }: DesktopMouthpieceButtonProps) => {
    const [isSnaptixOpen, setIsSnaptixOpen] = React.useState<boolean>(false);
    const btnRef = React.useRef<HTMLButtonElement>(null);

    React.useEffect(() => {
        if (btnRef.current) {
            btnRef.current.addEventListener('mouseenter', () => {
                if (item.caption.includes("Snaptix")) {
                    setIsSnaptixOpen(true);
                }
            });
            btnRef.current.addEventListener('mouseleave', () => {
                if (item.caption.includes("Snaptix")) {
                    setIsSnaptixOpen(false);
                }
            });
            btnRef.current.addEventListener('click', () => {
                setState(pickItem(item));
            });
        }
    }, [item.caption]);

    return (
        <button type={"button"}
                data-full-img-src={item.imgSrc}
                data-full-img-width={item.imgWidth}
                data-full-img-height={item.imgHeight}
                className={`${state.picked === true && state.selectedItem === item ? 'active' : ''} flex flex-col justify-end items-center`}
                data-category={item.category}
                onClick={() => {
                    setState(pickItem(item));
                }}
                ref={btnRef}
                style={{ height: `${item.imgPreviewHeight + 40}px` }}
        >
            <VadImage
                src={
                    item.imgPreviewSnaptixSrc
                        ? item.imgPreviewSnaptixSrc[isSnaptixOpen ? 'open' : 'closed']
                        : item.imgPreviewSrc
                }
                width={item.imgPreviewWidth}
                height={item.imgPreviewHeight}
                alt={item.caption}
                useOnlyOneDomain={true}
            />
            <span dangerouslySetInnerHTML={{ __html: item.caption }} />
        </button>
    );
}

interface MouthpieceImageProps {
    item: Mouthpiece;
}

const MouthpieceImage = ({ item }: MouthpieceImageProps) => {
    const [isSnaptixOpen, setIsSnaptixOpen] = React.useState<boolean>(false);
    const imageRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (imageRef.current) {
            imageRef.current.addEventListener('mouseenter', () => {
                setIsSnaptixOpen(true);
            });
            imageRef.current.addEventListener('mouseleave', () => {
                setIsSnaptixOpen(false);
            });
        }
    }, []);

    return (
        <div ref={imageRef}>
            <VadImage
                src={
                    item.imgSnaptixSrc
                        ? item.imgSnaptixSrc[isSnaptixOpen ? 'open' : 'closed']
                        : item.imgSrc
                }
                width={item.imgWidth}
                height={item.imgHeight}
                alt={item.caption}
                useOnlyOneDomain={true}
            />
        </div>
    );
}

export function MouthpiecesWrapperBlock({ header, list, features, footer }: MouthpiecesWrapperBlockProps)
{
    const updateState = React.useCallback((state: State, allItems: Mouthpiece[], category?: MouthpieceCategory) =>
    {
        if (state.allItems === allItems && state.category === category)
        {
            return state;
        }

        const items = category === undefined ? allItems : allItems.filter((item) => {
            if (Array.isArray(item.category)) {
                return item.category.some((itemCategory) => itemCategory === category);
            } else {
                return item.category === category;
            }
        });
        const selectedItem = state.selectedItem !== undefined && items.includes(state.selectedItem) ? state.selectedItem : items.length > 0 ? items[0] : undefined;

        return { ...state, allItems, items, category, selectedItem };
    }, []);

    const [state, setState] = React.useState<State>(() => updateState({ allItems: [], items: [] }, list, undefined));

    React.useEffect(() =>
    {
        setState(prevState => updateState(prevState, list, prevState.category));
    }, [list, updateState]);

    const handleCategoryClicked = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(event =>
    {
        const buttonCategory = (event.target as HTMLButtonElement).dataset["category"] as MouthpieceCategory;
        const category = mouthpieceCategories.includes(buttonCategory) ? buttonCategory : undefined;
        setState(prevState => updateState(prevState, prevState.allItems, category));
    }, [updateState]);

    const mobileSliderItemsProps = React.useMemo(() => 
        state.allItems.map((item, index) => ({
            key: String(index),
            imgPreviewSrc: item.imgPreviewSrc,
            imgPreviewWidth: item.imgPreviewWidth,
            imgPreviewHeight: item.imgPreviewHeight,
            caption: item.caption,
            onAction: () => setState(pickItem(item))
        }), []),
    [state.allItems]);

    return (<div className={mouthpieces.mouthpieces_section}>
        <div className="container">
            <div className="bg-card py-[40px] px-[24px] lg:p-20 mb-6">
                <div className={`${mouthpieces.mouthpieces_text} text-center`} dangerouslySetInnerHTML={{ __html: header }} />
                <div className={`${mouthpieces.mouthpieces_slider_wrapper} flex flex-col-reverse md:flex-row md:justify-between md:gap-2.5`}>
                    <div className={`${mouthpieces.mouthpieces_slider_main_image} ${mouthpieces[`main-image-${state.selectedItem?.imgWidth ?? 279}`]}`}>
                        {
                            state.selectedItem && <MouthpieceImage item={state.selectedItem} />
                        }
                    </div>
                    <div className={`hidden md:block ${mouthpieces.mouthpieces_slider_main_nav}`}>
                        <nav className={`${mouthpieces.mouthpieces_slider_categories}`}>
                            <button key="All" type={"button"} className={state.category === undefined ? "active" : undefined} onClick={handleCategoryClicked}>All</button>
                            {
                                mouthpieceCategories.map(category => <button className={state.category === category ? "active" : undefined} data-category={category} key={category} onClick={handleCategoryClicked} type={"button"}>{category}</button>)
                            }
                        </nav>
                        <nav className={`${mouthpieces.mouthpieces_slider_preview_list}`}>
                            {
                                state.items.map((item, key) =>
                                    <DesktopMouthpieceButton
                                        key={key}
                                        item={item}
                                        state={state}
                                        setState={setState}
                                    />
                                )}
                        </nav>
                    </div>
                    <div className={`flex md:hidden ${mouthpieces['mouthpieces_mobile_slider_wrapper']}`}>
                        <P.MouthpiecesMobileSlider
                            items={mobileSliderItemsProps}
                            selectedItemTitle={state.selectedItem?.caption ?? ""}
                        />
                    </div>
                </div>
                {footer}
            </div>
            {!SnaptixHidden && <P.AnnouncementSnaptixMouthpiece />}
            <div className="hidden lg:grid grid-cols-1 lg:grid-cols-2 gap-6">
                {features && features.map((feature: MouthpieceFeatureList, index) => (
                    <div key={index} className={`${mouthpieces.feature_media_text} bg-card`}>
                        <figure className={mouthpieces.feature_media}>
                            <VadImage src={feature.imgSrc} width={feature.imgWidth} height={feature.imgHeight} alt={feature.imgAlt} useOnlyOneDomain={true} />
                        </figure>
                        <div className={mouthpieces.feature_text}>
                            <div className={mouthpieces.pretitle}><span>{feature.pretitle}</span></div>
                            <div dangerouslySetInnerHTML={{ __html: feature.content }}></div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    </div>)
}